if (!customElements.get('product-protection-modal')) {
  class ProductProtectionModal extends HTMLElement {
    constructor() {
      super();
      this.opened = false;
      this.attachedHandles = new WeakSet();
      this.boundOnDocClickCapture = this.onDocClickCapture.bind(this);
      this.boundOnKeydown = this.onKeydown.bind(this);
      this.boundOnDomChange = this.onDomChange.bind(this);
      this.observer = null;
      this.scanScheduled = false;
    }

    connectedCallback() {
      this.bindCloseControls();
      this.scheduleScans();

      this.observer = new MutationObserver(this.boundOnDomChange);
      this.observer.observe(document.body, { childList: true, subtree: true });

      document.addEventListener('click', this.boundOnDocClickCapture, true);

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', (event) => {
          if (event.target && event.target.contains(this)) {
            this.bindCloseControls();
            this.scheduleScans();
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      document.removeEventListener('click', this.boundOnDocClickCapture, true);
      document.removeEventListener('keydown', this.boundOnKeydown);
      if (this.opened) {
        document.body.classList.remove('product-protection-modal--open');
      }
    }

    onDomChange() {
      if (this.scanScheduled) return;
      this.scanScheduled = true;
      requestAnimationFrame(() => {
        this.scanScheduled = false;
        this.scanAndBind();
        if (this.opened) this.suppressXcottonModal();
      });
    }

    scheduleScans() {
      this.scanAndBind();
      [200, 600, 1500, 3500].forEach((delay) => {
        setTimeout(() => this.scanAndBind(), delay);
      });
    }

    getSelectors() {
      const raw = this.dataset.triggerSelectors || '';
      return raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }

    getTriggerTextRegex() {
      const raw = this.dataset.triggerText || 'add product protection|product protection plan|product protection';
      const parts = raw
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
      if (parts.length === 0) return null;
      try {
        return new RegExp(parts.join('|'), 'i');
      } catch (e) {
        return null;
      }
    }

    isLikelyXcottonIcon(el) {
      if (!el) return false;
      try {
        return !!el.closest(
          '.shopify-app-block, [class*="xcotton" i], [class*="xc-insurance" i], [class*="post-purchase-guard" i], [data-xcotton], [id*="xcotton" i]'
        );
      } catch (e) {
        return false;
      }
    }

    // 商品交互控件：数量增减、加购与支付按钮、变体选择器。这些元素点击后必须
    // 执行它们自己的逻辑，绝不能被当成「信息图标」拦截 —— onDocClickCapture 在
    // 捕获阶段就 stopImmediatePropagation()，一旦误判，控件会彻底失效而不是
    // 「顺便也弹个窗」。
    isCommerceControl(el) {
      if (!el || !el.closest) return false;
      try {
        return !!el.closest(
          'quantity-input, .quantity, product-form, .product-form__buttons, variant-selects, .shopify-payment-button'
        );
      } catch (e) {
        return false;
      }
    }

    isNearProtectionText(el) {
      if (!el) return false;
      if (this.contains(el)) return false;
      const regex = this.getTriggerTextRegex();
      if (!regex) return false;
      let current = el.parentElement;
      for (let i = 0; i < 5 && current && current !== document.body; i++) {
        const text = (current.textContent || '').slice(0, 600);
        if (regex.test(text)) return true;
        current = current.parentElement;
      }
      return false;
    }

    looksLikeIcon(el) {
      if (!el || !el.tagName) return false;
      if (this.contains(el)) return false;
      try {
        if (el.matches('[aria-label*="close" i], [class*="close"], [class*="dismiss"]')) return false;
      } catch (e) {
        return false;
      }
      if (this.isCommerceControl(el)) return false;
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
      if (rect.width > 60 || rect.height > 60) return false;
      const tag = el.tagName.toLowerCase();
      if (['svg', 'button', 'i', 'span', 'a', 'div'].indexOf(tag) === -1) return false;

      // Xcotton 自己注入的元素仍然全量识别（这是本 section 的立身之本：
      // 用我们的弹窗替掉 Xcotton 的原生弹窗）。
      if (this.isLikelyXcottonIcon(el)) return true;

      try {
        // 只认真正的「信息入口」信号。此前这里还列了裸标签 svg / button / i，
        // 等价于「任何 60px 以内的按钮或图标都算信息图标」—— 于是
        // button.quantity__button（45px、无 aria-label、class 不含 info）
        // 被 scanByTextHeuristic 绑上 data-pp-bound="text"，因为它和保护计划
        // 标题共处 section.product__info-container（在标题往上 5 层之内）。
        if (
          el.matches(
            '[role="button"], [aria-label*="info" i], [aria-label*="learn" i], [aria-label*="more" i], [aria-label*="help" i], [title*="info" i], [class*="info" i], [class*="tooltip" i], [class*="help" i], [class*="question" i], [data-pp-bound]'
          )
        ) {
          return true;
        }
      } catch (e) {
        return false;
      }
      return false;
    }

    findTextContainers() {
      const regex = this.getTriggerTextRegex();
      if (!regex) return [];
      const containers = new Set();
      try {
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
          acceptNode: (node) => {
            if (this.contains(node)) return NodeFilter.FILTER_REJECT;
            const text = node.textContent;
            if (!text || !text.trim()) return NodeFilter.FILTER_REJECT;
            return regex.test(text) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
          },
        });
        let node;
        while ((node = walker.nextNode())) {
          if (node.parentElement) containers.add(node.parentElement);
        }
      } catch (e) {
        // ignore
      }
      return Array.from(containers);
    }

    scanAndBind() {
      const selectors = this.getSelectors();
      if (selectors.length > 0) {
        let nodes = [];
        try {
          nodes = document.querySelectorAll(selectors.join(','));
        } catch (e) {
          nodes = [];
        }
        nodes.forEach((el) => {
          if (this.attachedHandles.has(el)) return;
          if (!this.isLikelyXcottonIcon(el) && !this.isNearProtectionText(el)) return;
          this.attachedHandles.add(el);
          el.setAttribute('data-pp-bound', 'selector');
        });
      }

      this.scanByTextHeuristic();
    }

    scanByTextHeuristic() {
      const textContainers = this.findTextContainers();
      textContainers.forEach((textEl) => {
        let context = textEl;
        for (let i = 0; i < 5 && context && context !== document.body; i++) {
          const candidates = context.querySelectorAll(
            'svg, button, i, [role="button"], [aria-label*="info" i], [aria-label*="learn" i], [aria-label*="help" i], [class*="info" i], [class*="tooltip" i], [class*="help" i], [class*="question" i]'
          );
          candidates.forEach((cand) => {
            if (this.attachedHandles.has(cand)) return;
            if (!this.looksLikeIcon(cand)) return;
            this.attachedHandles.add(cand);
            cand.setAttribute('data-pp-bound', 'text');
          });
          context = context.parentElement;
        }
      });
    }

    onDocClickCapture(event) {
      const target = event.target;
      if (!target || !target.closest) return;
      if (this.contains(target)) return;

      // 命中判定之前先放行商品交互控件。looksLikeIcon 里的同一道防护只覆盖
      // data-pp-bound 这条路径；下面 target.closest(getSelectors()) 那条路径读的是
      // Theme Editor 里可自由填写的 trigger_selectors，一个宽泛值（如 "button"
      // 或 ".info"）配合 isNearProtectionText（往上 5 层查 textContent，在 PDP 上
      // 几乎必然命中）就会让数量加减号重新失效 —— 而那属于配置改动，不过代码
      // review。这里以「实际被点击的元素」为准，因为被 cancel 掉的正是它的默认行为。
      if (this.isCommerceControl(target)) return;

      let hit = null;

      try {
        hit = target.closest('[data-pp-bound]');
      } catch (e) {
        hit = null;
      }

      if (!hit) {
        const selectors = this.getSelectors();
        if (selectors.length > 0) {
          try {
            hit = target.closest(selectors.join(','));
          } catch (e) {
            hit = null;
          }
        }
      }

      if (!hit) return;

      if (
        !this.isLikelyXcottonIcon(hit) &&
        !this.isNearProtectionText(hit) &&
        hit.getAttribute('data-pp-bound') !== 'text'
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.show();
    }

    suppressXcottonModal() {
      const xcottonModalSelectors = [
        '.xcotton-modal',
        '.xcotton-popup',
        '.xc-modal',
        '.xc-insurance-modal',
        '.post-purchase-guard__modal',
        '[class*="xcotton" i][class*="modal" i]',
        '[class*="xcotton" i][class*="popup" i]',
        '[class*="xcotton" i][class*="dialog" i]',
        '[data-xcotton-modal]',
      ].join(',');
      let nodes = [];
      try {
        nodes = document.querySelectorAll(xcottonModalSelectors);
      } catch (e) {
        nodes = [];
      }
      nodes.forEach((m) => {
        if (m === this || this.contains(m)) return;
        m.style.setProperty('display', 'none', 'important');
      });
    }

    bindCloseControls() {
      this.querySelectorAll('[data-pp-close]').forEach((el) => {
        if (el.dataset.ppCloseBound) return;
        el.dataset.ppCloseBound = '1';
        el.addEventListener('click', (e) => {
          e.preventDefault();
          this.hide();
        });
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.hide();
      }
    }

    show() {
      if (this.opened) return;
      this.opened = true;
      this.removeAttribute('hidden');
      document.body.classList.add('product-protection-modal--open');
      document.addEventListener('keydown', this.boundOnKeydown);

      this.suppressXcottonModal();

      const card = this.querySelector('.section-product-protection-modal__card');
      requestAnimationFrame(() => {
        if (card) card.focus({ preventScroll: true });
      });

      this.dispatchEvent(new CustomEvent('product-protection-modal:open', { bubbles: true }));
    }

    hide() {
      if (!this.opened) return;
      this.opened = false;
      this.setAttribute('hidden', '');
      document.body.classList.remove('product-protection-modal--open');
      document.removeEventListener('keydown', this.boundOnKeydown);
      this.dispatchEvent(new CustomEvent('product-protection-modal:close', { bubbles: true }));
    }
  }

  customElements.define('product-protection-modal', ProductProtectionModal);
}
